import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import api, { errorMessage } from '../services/api';
import { Button, Card, CopyButton, ErrorBanner, Field, LoadingBlock, PageHeader, SectionTitle, inputClass, textareaClass } from '../components/ui';

const PLATFORMS = [
  { key: 'shopee', label: 'Shopee' },
  { key: 'tiktokshop', label: 'TikTok Shop' },
  { key: 'lazada', label: 'Lazada' }
];
const RATE_PATTERN = /^\d{1,3}(\.\d{1,2})?$/;
const validRate = (value) => RATE_PATTERN.test(value.trim()) && Number(value) <= 100;
// Bookmarklet cho Safari iPhone: hiện cookie đọc được bằng JS (không gồm cookie HttpOnly) để sao chép
// eslint-disable-next-line no-script-url -- đây là mã bookmarklet để người dùng sao chép, không chạy trong trang
const COOKIE_BOOKMARKLET = "javascript:prompt('Cookie Lazada: chọn tất cả rồi sao chép',document.cookie)";

const CheckLine = ({ label, check }) => (
  <div className="flex items-center gap-2 text-sm">
    <span className={check?.ok ? 'text-green-600' : 'text-red-600'}>{check?.ok ? '✓' : '✗'}</span>
    <span className="text-gray-700">{label}</span>
    {check && !check.ok && <span className="text-gray-500">({check.reason})</span>}
  </div>
);

const CookieStatus = ({ status }) => {
  if (!status) return <p className="mb-3 text-sm text-gray-500">Backend chưa kiểm tra cookie lần nào.</p>;
  const tone = status.alert ? 'border-red-200 bg-red-50 text-red-700'
    : status.state === 'ok' ? 'border-green-200 bg-green-50 text-green-700' : 'border-gray-200 bg-gray-50 text-gray-600';
  const text = status.alert
    ? `Lazada từ chối cookie từ ${formatTime(status.rejected_since)}. Tạo link và tìm sản phẩm Lazada đang lỗi: hãy dán cookie mới.`
    : status.state === 'ok' ? 'Cookie đang hoạt động.'
      : status.state === 'pending' ? 'Đã lưu cookie mới, backend sẽ kiểm tra trong vòng 30 phút.'
        : 'Lần kiểm tra gần nhất lỗi kết nối Lazada (chưa kết luận cookie chết).';
  return (
    <div className={`mb-3 rounded-xl border p-3 text-sm ${tone}`}>
      <div className="font-medium">{text}</div>
      <div className="mt-1 text-xs opacity-80">
        Kiểm tra lúc {formatTime(status.checked_at)}
        {status.last_ok_at ? ` · hoạt động gần nhất ${formatTime(status.last_ok_at)}` : ''}
      </div>
    </div>
  );
};

const formatTime = (value) => (value ? new Date(value).toLocaleString('vi-VN') : '');

const updatedHint = (setting) =>
  setting
    ? `Sửa lúc ${formatTime(setting.updated_at)}${setting.updated_by ? ` bởi ${setting.updated_by}` : ''}`
    : 'Chưa đặt: service đang dùng biến môi trường';

const RateInput = ({ label, setting, value, onChange }) => {
  const invalid = value !== '' && !validRate(value);
  return (
    <Field label={label} hint={invalid ? 'Nhập 0–100, tối đa 2 chữ số thập phân' : updatedHint(setting)}>
      <div className="relative">
        <input
          className={`${inputClass} pr-8 ${invalid ? 'border-red-400' : ''}`}
          inputMode="decimal"
          value={value}
          placeholder="Chưa đặt"
          onChange={(e) => onChange(e.target.value)}
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">%</span>
      </div>
    </Field>
  );
};

const SettingsPage = () => {
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery('admin-settings', async () => (await api.get('/admin/settings')).data.data);
  const [rates, setRates] = useState({ display: {}, payout: {} });
  const [cookie, setCookie] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Thử cookie đang dán với Lazada (tìm sản phẩm + tạo link), không lưu
  const testCookie = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data: res } = await api.post('/admin/settings/lazada-cookie/test', { cookie });
      setTestResult(res.data);
    } catch (e) {
      toast.error(errorMessage(e, 'Lỗi khi thử cookie'));
    } finally {
      setTesting(false);
    }
  };

  // Giá trị đang lưu → form ('' = chưa đặt)
  const saved = useMemo(() => {
    const pick = (group) => Object.fromEntries(Object.entries(group || {}).map(([k, v]) => [k, v ? v.value : '']));
    return { display: pick(data?.rates?.display), payout: pick(data?.rates?.payout) };
  }, [data]);
  useEffect(() => setRates(saved), [saved]);

  const setRate = (kind, name) => (value) => setRates((prev) => ({ ...prev, [kind]: { ...prev[kind], [name]: value } }));

  // Chỉ gửi tỷ lệ đã đổi; ô để trống không gửi (giữ giá trị cũ)
  const changedRates = {};
  for (const kind of ['display', 'payout']) {
    for (const [name, value] of Object.entries(rates[kind] || {})) {
      if (value.trim() !== '' && value.trim() !== saved[kind]?.[name]) {
        changedRates[kind] = { ...changedRates[kind], [name]: value.trim() };
      }
    }
  }
  const hasInvalid = ['display', 'payout'].some((kind) =>
    Object.values(rates[kind] || {}).some((value) => value !== '' && !validRate(value)));
  const hasChanges = Object.keys(changedRates).length > 0 || cookie.trim() !== '';

  const save = async () => {
    setSaving(true);
    try {
      const body = { rates: changedRates };
      if (cookie.trim()) body.lazada_cookie = cookie;
      const { data: res } = await api.put('/admin/settings', body);
      const changed = res.data?.changed || [];
      toast.success(changed.length ? `Đã lưu ${changed.length} cài đặt` : 'Không có giá trị nào thay đổi');
      setCookie('');
      queryClient.invalidateQueries('admin-settings');
    } catch (e) {
      toast.error(errorMessage(e, 'Lỗi khi lưu cài đặt'));
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return <LoadingBlock />;

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Cài đặt"
        subtitle="Tỷ lệ chia hoa hồng cho người dùng và cookie Lazada"
        actions={<Button onClick={save} loading={saving} disabled={!hasChanges || hasInvalid}>Lưu thay đổi</Button>}
      />
      <ErrorBanner message={error && errorMessage(error, 'Không tải được cài đặt')} onRetry={refetch} />

      <Card className="mb-4 p-4 md:p-5">
        <SectionTitle>Tỷ lệ chia hoa hồng</SectionTitle>
        <p className="mb-4 text-sm text-gray-500">
          <b>Hiển thị</b>: tiền hoàn ước tính khi người dùng dán link (áp dụng trong khoảng 1 phút).{' '}
          <b>Thực trả</b>: cron ghi vào đơn mới ở lượt đồng bộ sau. Đơn đã có giữ nguyên tỷ lệ lúc tạo.
        </p>
        <div className="space-y-4">
          {PLATFORMS.map((p) => (
            <div key={p.key} className="grid grid-cols-1 gap-3 sm:grid-cols-[120px_1fr_1fr] sm:items-start">
              <div className="pt-1 text-sm font-semibold text-gray-800 sm:pt-7">{p.label}</div>
              <RateInput label="Hiển thị" setting={data?.rates?.display?.[p.key]}
                value={rates.display[p.key] ?? ''} onChange={setRate('display', p.key)} />
              <RateInput label="Thực trả" setting={data?.rates?.payout?.[p.key]}
                value={rates.payout[p.key] ?? ''} onChange={setRate('payout', p.key)} />
            </div>
          ))}
          <div className="grid grid-cols-1 gap-3 border-t border-gray-100 pt-4 sm:grid-cols-[120px_1fr_1fr] sm:items-start">
            <div className="pt-1 text-sm font-semibold text-gray-800 sm:pt-7">Đơn thất lạc</div>
            <RateInput label="Thực trả" setting={data?.rates?.payout?.lost_order}
              value={rates.payout.lost_order ?? ''} onChange={setRate('payout', 'lost_order')} />
          </div>
        </div>
      </Card>

      <Card className="p-4 md:p-5">
        <SectionTitle>Cookie Lazada Adsense</SectionTitle>
        <CookieStatus status={data?.lazada_cookie_status} />
        <p className="mb-3 text-sm text-gray-500">
          {data?.lazada_cookie?.configured
            ? `Đang dùng cookie lưu ở cài đặt. ${updatedHint(data.lazada_cookie)}.`
            : 'Chưa lưu cookie ở cài đặt: backend đang dùng biến môi trường LAZADA_COOKIE.'}{' '}
          Cookie đã lưu không hiển thị lại. Dán cookie mới để thay (backend nhận trong khoảng 30 giây).
        </p>
        <Field label="Cookie mới" hint="Chuỗi Cookie header từ adsense.lazada.vn, dạng ten=gia_tri; ten2=gia_tri2">
          <textarea
            className={`${textareaClass} h-28 font-mono text-xs`}
            value={cookie}
            onChange={(e) => { setCookie(e.target.value); setTestResult(null); }}
            placeholder="Để trống nếu không đổi"
            autoComplete="off"
            spellCheck={false}
          />
        </Field>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={testCookie} loading={testing} disabled={!cookie.trim()}>Thử cookie</Button>
          <span className="text-xs text-gray-400">Gọi Lazada tìm 1 sản phẩm và tạo link, không lưu cookie</span>
        </div>
        {testResult && (
          <div className="mt-3 space-y-1 rounded-xl bg-gray-50 p-3">
            <CheckLine label="Tìm sản phẩm" check={testResult.search} />
            {testResult.search?.ok && <CheckLine label="Tạo link affiliate" check={testResult.link} />}
            <div className="pt-1 text-xs text-gray-500">
              {testResult.cookie_names.length} cookie: {testResult.cookie_names.join(', ')}
            </div>
          </div>
        )}

        <details className="mt-4 text-sm text-gray-600">
          <summary className="cursor-pointer font-medium text-gray-700">Lấy cookie trên iPhone (Safari)</summary>
          <ol className="mt-2 list-decimal space-y-1 pl-5">
            <li>Sao chép đoạn mã bookmarklet bên dưới.</li>
            <li>Trong Safari, đánh dấu trang bất kỳ, rồi sửa dấu trang đó: đổi tên thành "Cookie Lazada", dán đoạn mã vào ô địa chỉ.</li>
            <li>Mở adsense.lazada.vn (đã đăng nhập), mở Dấu trang và bấm "Cookie Lazada".</li>
            <li>Chọn tất cả chữ trong hộp hiện ra, sao chép, dán vào ô "Cookie mới" ở trên rồi bấm Thử cookie.</li>
          </ol>
          <div className="mt-2 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-lg bg-gray-100 px-2 py-1 text-xs">{COOKIE_BOOKMARKLET}</code>
            <CopyButton value={COOKIE_BOOKMARKLET} />
          </div>
          <p className="mt-2 text-xs text-gray-400">
            Cách này chỉ lấy được cookie mà JavaScript đọc được. Nếu thử báo lỗi, Lazada cần thêm cookie HttpOnly và phải lấy cookie từ máy tính.
          </p>
        </details>
      </Card>
    </div>
  );
};

export default SettingsPage;
